import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { getLocalStorageKey } from '../../utils/storageUtils';
import {
  addDashboardWidget,
  DashboardLayout,
  DashboardWidgetId,
  DEFAULT_DASHBOARD_LAYOUT,
  moveDashboardWidget,
  normalizeDashboardLayout,
  removeDashboardWidget,
} from './dashboardWidgets';

type DashboardLayoutController = {
  visibleWidgetIds: DashboardWidgetId[];
  hiddenWidgetIds: DashboardWidgetId[];
  moveWidget: (activeId: DashboardWidgetId, overId: DashboardWidgetId) => void;
  removeWidget: (widgetId: DashboardWidgetId) => void;
  addWidget: (widgetId: DashboardWidgetId) => void;
};

const readLayout = (storageKey: string): DashboardLayout => {
  if (!storageKey) {
    return DEFAULT_DASHBOARD_LAYOUT;
  }

  try {
    const storedValue = localStorage.getItem(storageKey);
    return normalizeDashboardLayout(storedValue ? JSON.parse(storedValue) : undefined);
  } catch {
    return DEFAULT_DASHBOARD_LAYOUT;
  }
};

const createStorageKey = (fleetId: string, userId: string): string => {
  if (!fleetId || !userId) {
    return '';
  }

  return getLocalStorageKey(`dashboard-layout-${fleetId}-${encodeURIComponent(userId.toLowerCase())}`);
};

function useDashboardLayout(fleetId: string, userId: string): DashboardLayoutController {
  const storageKey = useMemo(() => createStorageKey(fleetId, userId), [fleetId, userId]);
  const [layout, setLayout] = useState<DashboardLayout>(() => readLayout(storageKey));

  useEffect(() => {
    setLayout(readLayout(storageKey));
  }, [storageKey]);

  const updateLayout = useCallback((updater: (current: DashboardLayout) => DashboardLayout) => {
    setLayout((current) => {
      const next = normalizeDashboardLayout(updater(current));

      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          return next;
        }
      }

      return next;
    });
  }, [storageKey]);

  const moveWidget = useCallback((activeId: DashboardWidgetId, overId: DashboardWidgetId) => {
    updateLayout((current) => moveDashboardWidget(current, activeId, overId));
  }, [updateLayout]);

  const removeWidget = useCallback((widgetId: DashboardWidgetId) => {
    updateLayout((current) => removeDashboardWidget(current, widgetId));
  }, [updateLayout]);

  const addWidget = useCallback((widgetId: DashboardWidgetId) => {
    updateLayout((current) => addDashboardWidget(current, widgetId));
  }, [updateLayout]);

  const visibleWidgetIds = useMemo(
    () => layout.order.filter((id) => !layout.hidden.includes(id)),
    [layout],
  );

  return {
    visibleWidgetIds,
    hiddenWidgetIds: layout.hidden,
    moveWidget,
    removeWidget,
    addWidget,
  };
}

export default useDashboardLayout;
